/**
 * Organization Activity Model
 *
 * Represents activity log entries for an organization.
 * Used for displaying recent activity in the dashboard.
 *
 * Reference: /agent-os/specs/2026-01-16-dashboard-updates/spec.md
 */

/**
 * Event Type
 * Defines the types of activities that can be logged
 */
export type EventType =
  | 'flight.created'
  | 'flight.started'
  | 'flight.completed'
  | 'drone.created'
  | 'drone.status_changed'
  | 'drone.assigned'
  | 'team.member_joined'
  | 'customer.created'
  | 'field.created'
  | 'custom_chemical.created'
  | 'chemical_template.created'
  | 'report.generated'
  | 'safety_test.completed'
  | 'safety_test.due'
  | 'ticket.created'
  | 'ticket.closed'
  | 'ticket.reopened'
  | 'ticket.reply'
  | 'appointment.requested'
  | 'appointment.scheduled'
  | 'appointment.cancelled'
  | 'appointment.completed'
  | 'work_order.created'
  | 'work_order.in_progress'
  | 'work_order.completed'
  | 'work_order.cancelled'
  | 'certification.uploaded'
  | 'certification.renewed';

/**
 * Resource Type
 * Defines the types of resources that activities can link to
 */
export type ResourceType =
  | 'spray_record'
  | 'drone'
  | 'profile'
  | 'customer'
  | 'field'
  | 'custom_chemical'
  | 'chemical_template'
  | 'faa_report'
  | 'state_report'
  | 'field_test_submission'
  | 'support_ticket'
  | 'service_appointment'
  | 'work_order'
  | 'certification';

/**
 * Role Bitwise Constants
 * Used for visibility_roles bitwise operations
 */
export const ROLE_OWNER = 1;
export const ROLE_CHIEF_SUPERVISOR = 2;
export const ROLE_PIC = 4;
export const ROLE_PILOT = 8;

/**
 * Convenience constant for all roles
 */
export const ALL_ROLES = ROLE_OWNER | ROLE_CHIEF_SUPERVISOR | ROLE_PIC | ROLE_PILOT; // 15

/**
 * Organization Activity interface
 * Matches the backend OrganizationActivity model
 */
export interface OrganizationActivity {
  id: string;
  organizationId: string;
  profileId: string | null;
  eventType: EventType;
  message: string;
  resourceType: ResourceType | null;
  resourceId: string | null;
  visibilityRoles: number;
  isHighPriority: boolean;
  createdAt: string;
}

/**
 * Activity query options
 */
export interface ActivityQueryOptions {
  limit?: number;
  cursor?: string; // ISO timestamp
  eventTypes?: EventType[];
}

/**
 * Paginated activities response
 */
export interface PaginatedActivities {
  activities: OrganizationActivity[];
  nextCursor: string | null;
  hasMore: boolean;
}

/**
 * Dashboard stats interface
 */
export interface DashboardStats {
  totalDrones: number;
  activeDrones: number;
  flightHours: number;
  totalAcresSprayed: number;
  upcomingFlights: number;
}

/**
 * WebSocket activity.new event payload
 */
export interface ActivityNewPayload {
  activityId: string;
  eventType: EventType;
  message: string;
  resourceType: ResourceType | null;
  resourceId: string | null;
  visibilityRoles: number;
  profileId: string | null;
  createdAt: string;
}

/**
 * Map event type to icon class
 */
export function getActivityIcon(eventType: EventType): string {
  switch (eventType) {
    case 'flight.created':
    case 'flight.started':
    case 'flight.completed':
      return 'icon-drone';
    case 'drone.created':
    case 'drone.status_changed':
    case 'drone.assigned':
      return 'icon-drone';
    case 'team.member_joined':
      return 'bi bi-person-plus';
    case 'customer.created':
      return 'bi bi-person';
    case 'field.created':
      return 'bi bi-geo-alt';
    case 'custom_chemical.created':
      return 'bi bi-droplet';
    case 'chemical_template.created':
      return 'bi bi-file-earmark-plus';
    case 'report.generated':
      return 'bi bi-file-earmark-text';
    case 'safety_test.completed':
    case 'safety_test.due':
      return 'bi bi-clipboard-check';
    case 'ticket.created':
      return 'bi bi-ticket-detailed';
    case 'ticket.closed':
      return 'bi bi-check-circle';
    case 'ticket.reopened':
      return 'bi bi-arrow-counterclockwise';
    case 'ticket.reply':
      return 'bi bi-chat-left-text';
    case 'appointment.requested':
      return 'bi bi-calendar-plus';
    case 'appointment.scheduled':
      return 'bi bi-calendar-check';
    case 'appointment.cancelled':
      return 'bi bi-calendar-x';
    case 'appointment.completed':
      return 'bi bi-calendar2-check';
    case 'work_order.created':
    case 'work_order.in_progress':
    case 'work_order.completed':
    case 'work_order.cancelled':
      return 'bi bi-wrench';
    case 'certification.uploaded':
    case 'certification.renewed':
      return 'bi bi-file-earmark-check';
    default:
      return 'bi bi-activity';
  }
}

/**
 * Map resource type to route path
 * Routes must match app-routing.module.ts definitions
 */
export function getResourceRoute(resourceType: ResourceType | null, resourceId: string | null): string | null {
  if (!resourceType || !resourceId) return null;

  switch (resourceType) {
    case 'spray_record':
      return `/logbook/spray-records/${resourceId}/view`;
    case 'drone':
      return `/drones/${resourceId}/view`;
    case 'customer':
      return `/customers/${resourceId}/view`;
    case 'field':
      return `/customers/fields/${resourceId}/view`;
    case 'custom_chemical':
      // Custom chemicals are managed in settings
      return `/settings/chemicals`;
    case 'chemical_template':
      // Chemical templates are managed in settings
      return `/settings/chemical-templates`;
    case 'faa_report':
      // FAA reports don't have individual view pages, go to list
      return `/logbook/reports/faa`;
    case 'state_report':
      // State reports don't have individual view pages, go to list
      return `/logbook/reports/state`;
    case 'field_test_submission':
      return `/logbook/testing/field/${resourceId}`;
    case 'support_ticket':
      return `/maintenance/support-tickets/${resourceId}`;
    case 'service_appointment':
      return `/maintenance/service-appointments/${resourceId}`;
    case 'work_order':
      return `/maintenance/work-orders/${resourceId}`;
    case 'certification':
      return `/certifications`;
    case 'profile':
      // Team/profile page not yet implemented
      return null;
    default:
      return null;
  }
}

/**
 * Check if user role has visibility for an activity
 */
export function isActivityVisibleToRole(activity: OrganizationActivity, userRole: number): boolean {
  return (activity.visibilityRoles & userRole) !== 0;
}
